import { DateTime } from 'luxon'
import React, { Fragment, FunctionComponent, useEffect, useState } from 'react'
import { Row, Col } from 'react-grid-system'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'

import {
  StyledDetailsContainer,
  StyledDetailsItem,
  StyledOrderDetail,
  StyleOpaAction
} from './MaOrderDetail.styles'
import { ClinTheme } from '../../ClinTheme'
import { ClinAnnounceBar } from '../../components/ClinAnnounceBar'
import {
  AnnounceMode,
  IClinAnnounceBarProps
} from '../../components/ClinAnnounceBar/ClinAnnounceBar'
import { ClinButton } from '../../components/ClinButton'
import { ClinIcon } from '../../components/ClinIcon'
import { ClinIconPathName } from '../../components/ClinIcon/ClinIcon.paths'
import { INewFeature } from '../../components/ClinNewFeatureTooltip/ClinNewFeatureTooltip.types'
import { ClinPageContentFrame } from '../../components/ClinPageContentFrame'
import { ClinSpacer } from '../../components/ClinSpacer'
import { ClinText } from '../../components/ClinText'
import { TypographyVariant } from '../../components/ClinText/ClinText.styles'
import i18n from '../../i18n/config'
import { IHoldDetails } from '../../services/Holds/holds.types'
import { OrderStatus } from '../../types/OrderStatus'
import {
  HoldDto,
  OrderDto,
  OrderLineDto,
  OrderTrackingInfoDto,
  ProgramCatalogDto,
  UploadedDocumentDto
} from '../../types/swaggerTypes'
import {
  getOrderItems,
  getReassignedOrderItems,
  isProductTemperatureTracker
} from '../OpaCheckout/OpaCheckout.models'
import { getCarrierType } from '../OrderPages/OrderDetail/OrderDetail.model'
import {
  OrderDetailHeaderContainer,
  StyledActions,
  StyledButtonLabel,
  StyledClinPrimaryButton,
  StyledClinSecondaryButton,
  StyledHeaderClinPageContentFrame,
  StyledOrderBar,
  StyledOrderDetailDeliveryDateContainer,
  StyledOrderDetailDeliveryDateContent,
  StyledOrderDetailDeliveryDateIconContainer,
  StyledOrderDetailEstimatedDeliveryDateButton,
  StyledOrderDetailHeaderContainer,
  StyledOrderDetailHeaderContent,
  StyledOrderDetailHeaderNavigation,
  StyledOrderDetailHeaderTitle,
  StyledProductDetailsSection
} from '../OrderPages/OrderDetail/OrderDetail.styles'
import { StyledSpinnerContainer } from '../../components/ClinSpinner/ClinSpinner.styles'
import { ClinSpinner } from '../../components/ClinSpinner'
import { Tooltip } from 'react-tippy'
import { compareTwoDates } from '../../utils/dateUtils'
import { getOrderById } from '../../services/ApiService'
import analyticsServiceSingleton from '../../services/Analytics/initAnalytics'
import { ClinToastBar } from '../../components/ClinToastBar'
import {
  FixedWidthCell,
  ProductInformationBox,
  StyledClinPatientTableHeaderCell,
  StyledClinTable,
  StyledClinTableBodyCell,
  StyledClinTableExpandedRow,
  StyledClinTableRow,
  StyledClinTableToggleRowButton,
  StyledDesktopWrapper,
  StyledProductInformationBox,
  StyledStatusCell,
  StyledStrengthCell
} from '../OrderPages/OrderDetail/OrderProductDetails.styles'
import StatusColumn from '../OrderPages/Orders/StatusColumn/StatusColumn'
import { numberToCurrencyString } from '../../utils/numberToCurrencyString'
import { getBrowserLocale } from '../../utils/getBrowserLocale'
import { StyledClinTableOrderToggleWrapper } from '../Patients/PatientDashboard/PatientDashboard.styles'
import { ClinTableOrderToggle } from '../../components/ClinTableOrderToggle'
import ProductInformation from './ProductInformation/ProductInformation'
import { ClinTableBodyCell } from '../../components/ClinTable/ClinTable'
import OrderDelivery from '../OrderPages/OrderDetail/OrderDelivery/OrderDelivery'
import OrderSummary from '../OrderPages/OrderDetail/OrderSummary/OrderSummary'
import { isOrderEligibleForInvoice } from '../../utils/orderUtils'
import { shippedStatuses, shouldShowCancel } from './MaOrderDetail.models'
import ReassignedProducts from './ReassignedProducts/ReassignedProducts'

interface IMaOrderDetailProps {
  /** Whether order is loading or not  */
  isLoading: boolean
  /** Order details */
  order: OrderDto | undefined
  /** Program details */
  program: ProgramCatalogDto | undefined
  /** Provides order level holds to display above the order */
  orderLevelHolds?: IHoldDetails[]
  /** List of new features for this page **/
  newFeaturesList?: INewFeature[]
  /** patientAccessFormUrl */
  patientAccessFormUrl: string | undefined
  /** When download invoice **/
  isDownloadingInvoice: boolean
  /** Whether treating physician is associated to the user or not */
  isPhysicianAssociated: boolean
  isEnrolledProgram: boolean
  /** Displays an announce at the top of a page **/
  orderAnnounce?: IClinAnnounceBarProps
  /** Displays any tracking info at top or order lines **/
  trackingInfo?: OrderTrackingInfoDto
  isStockOrder?: boolean
  /** Handle click on "Completed Form" button */
  handlePatientAccessForm: () => void
  /** Handle click on "Cancel Order" button */
  handleCancelOrder: () => void
  /** Handle click on "Order Resupply" button */
  handleOrderResupply: () => void
  /** Request to go back to a page */
  handleGoBack: () => void
  /** Call back when file is uploaded for a hold */
  handleFileUploadedForOrderAndHold?: (
    orderId: string,
    orderLine: OrderLineDto,
    hold: HoldDto,
    document: UploadedDocumentDto
  ) => void
  /** Call back when file is deleted for a hold */
  handleFileDeleteForOrderAndHold?: (
    orderLine: OrderLineDto,
    hold: HoldDto,
    documentId?: number
  ) => void
  /** Handles the dismissal of the announcement bar **/
  handleDismissAnnounce?: () => void
  /** Handles a request to email order details **/
  handleEmailOrderDetails?: () => void
  handleTrackingLinkClicked: (orderLine: OrderLineDto, language: string) => void
}

const handleTrackOrder = async (orderNumber: string) => {
  try {
    const response = await getOrderById(orderNumber)
    const orderDto: OrderDto = response.data as OrderDto

    const orderLineWithTrackingLink = orderDto.lines.find(
      (line) => line.trackingInfo?.aftershipTrackingLink !== null
    )
    const trackingLink =
      orderLineWithTrackingLink?.trackingInfo?.aftershipTrackingLink
    if (trackingLink) {
      window.open(trackingLink, '_blank')
    }
  } catch (error: any) {
    analyticsServiceSingleton.trackError(
      error,
      JSON.parse(localStorage.getItem('current_user') || '{}')
    )
  }
}

// Helper functions need to be defined inside component to access translation
export const MaOrderDetail: FunctionComponent<IMaOrderDetailProps> = ({
  isLoading,
  order,
  program,
  patientAccessFormUrl,
  isPhysicianAssociated,
  isEnrolledProgram,
  isDownloadingInvoice,
  handlePatientAccessForm,
  handleCancelOrder,
  handleOrderResupply,
  handleGoBack,
  handleEmailOrderDetails,
  orderAnnounce,
  handleDismissAnnounce,
  handleTrackingLinkClicked,
  handleFileUploadedForOrderAndHold,
  handleFileDeleteForOrderAndHold,
  isStockOrder = false
}) => {
  const history = useHistory()
  const { t } = useTranslation()

  const [expandedRows, setExpandedRows] = useState<string[]>([])
  const [hoveredRow, setHoveredRow] = useState<string | null>(null)

  const isInvoiceAvailable = isOrderEligibleForInvoice(order)

  const hasAftershipTracking = (orderLine: OrderLineDto): boolean => {
    return !!(
      orderLine.trackingInfo?.waybillNumber &&
      getCarrierType(orderLine.trackingInfo?.freightCarrier) === 'Aftership'
    )
  }

  const showTrackingInfo = (orderLine: OrderLineDto): React.ReactNode => {
    if (hasAftershipTracking(orderLine)) {
      return orderLine.trackingInfo?.aftershipTrackingLink
        ? t('orders:actions.track_order')
        : t('orders:actions.tracking_link_pending')
    }
    return t('orders:actions.tracking_link_pending')
  }

  const isDeliveredOrderWithAftership = (orderLine: OrderLineDto): boolean => {
    const freightCarrier = getCarrierType(
      orderLine.trackingInfo?.freightCarrier
    )
    return (
      order?.orderStatus === OrderStatus.Delivered &&
      (freightCarrier === 'Aftership' || freightCarrier === 'Partial')
    )
  }

  // Extract basic data
  const patientNumber = order?.lines[0].patientNumber
  const physician = order?.lines[0].physician

  const orderedItems: OrderLineDto[] = getOrderItems(order)
  const reassignedItems: OrderLineDto[] = getReassignedOrderItems(order)

  const filteredOrderLines = orderedItems.filter(
    (orderLine) => !isProductTemperatureTracker(orderLine.skuCatalogItem?.item)
  )

  const getLatestDeliveryDate = (orderLine: OrderLineDto): string | null => {
    let latestDate: string | null = null
    if (orderLine.trackingInfo?.estimatedDeliveryDate) {
      latestDate = orderLine.trackingInfo.estimatedDeliveryDate
    }
    if (orderLine.expectedDate) {
      if (!latestDate) {
        latestDate = orderLine.expectedDate
      } else {
        latestDate = compareTwoDates(latestDate, orderLine.expectedDate)
      }
    }

    return latestDate
  }

  const formatDeliveryDate = (
    dateString: string | null,
    language: string
  ): React.ReactNode => {
    if (!dateString) {
      return t('glossary:not_available')
    }

    return DateTime.fromISO(dateString)
      .setLocale(language)
      .toFormat('cccc, MMM d')
  }

  const getLatestDeliveryDateFromAllOrderLines = (): string | null => {
    if (!order || !order.lines) return null
    let latestDate: string | null = null
    order.lines
      .filter((ol) => ol.medicationFlag === 'Y')
      .forEach((orderLine) => {
        const lineLatestDate = getLatestDeliveryDate(orderLine)

        if (lineLatestDate) {
          if (!latestDate) {
            latestDate = lineLatestDate
          } else {
            latestDate = compareTwoDates(latestDate, lineLatestDate)
          }
        }
      })

    return latestDate
  }

  const getOrderLineWithLatestDeliveryDate = (): OrderLineDto | null => {
    if (!order || !order.lines) return null

    const latestDate = getLatestDeliveryDateFromAllOrderLines()
    if (!latestDate) return null

    return (
      order.lines
        .filter((ol) => ol.medicationFlag === 'Y')
        .find((orderLine) => {
          const lineLatestDate = getLatestDeliveryDate(orderLine)
          return lineLatestDate === latestDate
        }) || null
    )
  }

  const handleSelectProduct = (orderLine: OrderLineDto) => {
    history.push(`/programs/access-programs/${orderLine.programId}`)
  }

  const handleMouseEnter = (id: string) => {
    setHoveredRow(id)
  }
  const handleMouseLeave = () => {
    setHoveredRow(null)
  }

  const isDisableResupply = (order: OrderDto) => {
    return (
      !isEnrolledProgram ||
      isStockOrder ||
      !shippedStatuses.includes(order.orderStatus as OrderStatus)
    )
  }

  const getProductDetailsColumns = (order: OrderDto) => [
    {
      name: 'strength',
      viewName: 'Strength',
      translationKey: 'glossary:strength',
      width: '100%',
      minWidth: '145px',
      maxWidth: '165px',
      isSortable: false,
      renderValue: (orderLine: OrderLineDto) => (
        <>
          {orderLine.skuCatalogItem?.products.map((product: any) => (
            <StyledStrengthCell key={`strength-item-${product.genericName}`}>
              <ClinText
                variant={TypographyVariant.SmallUI}
                lineHeight={ClinTheme.lineHeights.body}
                fontSize={ClinTheme.fontSizes[1]}
              >
                {product.genericName}
              </ClinText>
              <ClinText
                lineHeight={ClinTheme.lineHeights.body}
                fontSize={ClinTheme.fontSizes[1]}
              >
                {product.strength}
              </ClinText>
            </StyledStrengthCell>
          ))}
        </>
      )
    },
    {
      name: 'dosageFormAndPackSize',
      viewName: 'Dosage form and pack size',
      translationKey: 'glossary:dosage_and_packsize',
      width: '100%',
      minWidth: '115px',
      maxWidth: '180px',
      isSortable: false,
      renderValue: (orderLine: OrderLineDto) => (
        <>
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.body}
          >
            {orderLine.skuCatalogItem?.item.formulation}
          </ClinText>
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.body}
          >
            {orderLine.skuCatalogItem?.item.packSize}
          </ClinText>
        </>
      )
    },
    {
      name: 'status',
      viewName: 'Status',
      translationKey: 'glossary:status',
      width: '100%',
      minWidth: '158px',
      maxWidth: '168px',
      renderValue: (orderLine: OrderLineDto) => {
        return (
          <StyledStatusCell>
            <StatusColumn orderStatus={orderLine.status ?? order.orderStatus} />
          </StyledStatusCell>
        )
      }
    },
    {
      name: 'price',
      viewName: 'Pack price',
      translationKey: 'glossary:pack_price',
      width: '100%',
      isSortable: false,
      renderValue: (orderLine: OrderLineDto) => {
        return (
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.body}
          >
            {numberToCurrencyString(
              orderLine.unitSellingPrice,
              getBrowserLocale(),
              {
                style: 'currency',
                currency: order?.currencyCode ? order?.currencyCode : 'GBP',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }
            ) ?? '--'}
          </ClinText>
        )
      }
    },
    {
      name: 'quantity',
      viewName: 'Quantity',
      translationKey: 'order_detail:quantity',
      width: '100%',
      isSortable: false,
      renderValue: (orderLine: OrderLineDto) => {
        return (
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.body}
          >
            {orderLine.quantities.ordered ?? '--'}
          </ClinText>
        )
      }
    },
    {
      name: 'total',
      viewName: 'Total',
      translationKey: 'order_detail:total_cell',
      width: '100%',
      isSortable: false,
      renderValue: (orderLine: OrderLineDto) => {
        return (
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.body}
            fontWeight={ClinTheme.fontWeights.bold}
          >
            {numberToCurrencyString(
              orderLine.totals.subTotal,
              getBrowserLocale(),
              {
                style: 'currency',
                currency: order?.currencyCode ? order?.currencyCode : 'GBP',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }
            ) ?? '--'}
          </ClinText>
        )
      }
    },
    {
      name: 'toggle',
      viewName: 'Actions',
      translationKey: 'order_detail:actions',
      width: '100%',
      isSortable: false,
      renderValue: (orderLine: OrderLineDto) => (
        <StyledActions onClick={(e) => e.stopPropagation()}>
          <StyledClinTableToggleRowButton
            isToggled={expandedRows.includes(
              `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
            )}
            onClick={() => {
              handleToggleRow &&
                handleToggleRow(
                  `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                )
            }}
          />
        </StyledActions>
      )
    }
  ]

  const handleToggleRow = (itemId: string) => {
    setExpandedRows((prev: string[]) =>
      prev.includes(itemId)
        ? prev.filter((id: string) => id !== itemId)
        : [...prev, itemId]
    )
  }

  const handleTrackingLinkClick = (orderLine: OrderLineDto) => {
    if (
      hasAftershipTracking(orderLine) &&
      !!orderLine.trackingInfo?.aftershipTrackingLink &&
      order?.orderNumber
    ) {
      handleTrackOrder(order.orderNumber)
      handleTrackingLinkClicked(orderLine, i18n.language)
    }
  }

  useEffect(() => {
    const orderLines = order?.lines?.filter((ol) => ol.medicationFlag === 'Y')
    if (orderLines?.length === 1 && expandedRows.length === 0) {
      const orderLine = orderLines[0]
      handleToggleRow(`${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`)
    }
  }, [order])

  if (!order) return null

  const columns = getProductDetailsColumns(order)

  return (
    <ClinPageContentFrame isFullWidth={true}>
      {isLoading && (
        <Row justify="center">
          <Col width="auto">
            <StyledSpinnerContainer>
              <ClinSpinner size={ClinTheme.space[7]} />
            </StyledSpinnerContainer>
          </Col>
        </Row>
      )}

      {!isLoading && order?.lines && (
        <ClinPageContentFrame isFullWidth={true}>
          <StyledOrderDetail>
            <OrderDetailHeaderContainer className="order-detail-header">
              <StyledHeaderClinPageContentFrame>
                <StyledOrderDetailHeaderContainer>
                  <StyledOrderDetailHeaderNavigation>
                    {handleGoBack && (
                      <ClinButton
                        onClick={() => handleGoBack()}
                        variant="linkWithIcon"
                      >
                        <ClinIcon
                          style={{ marginRight: ClinTheme.space[2] }}
                          iconSize={ClinTheme.fontSizes[3]}
                          iconName={ClinIconPathName.ArrowLeft}
                        />
                        {t('common:buttons.back_button_text')}
                      </ClinButton>
                    )}
                    <StyledOrderDetailHeaderTitle
                      marginTop={ClinTheme.space[1]}
                      marginBottom={ClinTheme.space[1]}
                      variant={TypographyVariant.H3}
                    >
                      {t('order_detail:order_number', {
                        orderNumber: order.orderNumber
                      })}
                    </StyledOrderDetailHeaderTitle>
                  </StyledOrderDetailHeaderNavigation>
                  <StyledOrderDetailHeaderContent>
                    <Tooltip
                      theme="light"
                      position="bottom"
                      trigger="mouseenter"
                      arrow={true}
                      disabled={isInvoiceAvailable || isDownloadingInvoice}
                      title={
                        isInvoiceAvailable || isDownloadingInvoice
                          ? ''
                          : t('order_detail:invoice_tooltip')
                      }
                    >
                      <ClinButton
                        className="download-invoice-button"
                        variant="primary"
                        disabled={!isInvoiceAvailable || isDownloadingInvoice}
                        onClick={() =>
                          handleEmailOrderDetails && handleEmailOrderDetails()
                        }
                      >
                        {isDownloadingInvoice && (
                          <ClinSpinner size={ClinTheme.space[3]} />
                        )}
                        <StyledButtonLabel>
                          {t('order_detail:download_invoice')}
                        </StyledButtonLabel>
                      </ClinButton>
                    </Tooltip>
                    {(() => {
                      const latestDate =
                        getLatestDeliveryDateFromAllOrderLines()
                      const orderLineWithLatestDate =
                        getOrderLineWithLatestDeliveryDate()
                      const shouldShowDeliveryDate =
                        orderLineWithLatestDate &&
                        latestDate &&
                        order?.orderStatus !== OrderStatus.Cancelled &&
                        order?.orderStatus !== OrderStatus.Delivered &&
                        order?.orderStatus !== OrderStatus.Shipped &&
                        !isDeliveredOrderWithAftership(orderLineWithLatestDate)

                      if (!shouldShowDeliveryDate) {
                        return null
                      }

                      return (
                        <StyledOrderDetailDeliveryDateContainer>
                          <StyledOrderDetailDeliveryDateIconContainer>
                            <ClinIcon
                              viewBox="0 0 37 35"
                              iconHeight={'32px'}
                              iconSize={'32px'}
                              iconName={ClinIconPathName.DeliveryBox}
                              iconFill={ClinTheme.colors.primaryMid}
                            />
                          </StyledOrderDetailDeliveryDateIconContainer>
                          <StyledOrderDetailDeliveryDateContent>
                            <Fragment>
                              <ClinText
                                fontSize={ClinTheme.fontSizes[0]}
                                lineHeight={'16px'}
                                fontWeight={ClinTheme.fontWeights.medium}
                                color={ClinTheme.colors.primaryMid}
                              >
                                {t('order_detail:estimated_delivery')}
                              </ClinText>
                              <ClinText
                                fontSize={ClinTheme.fontSizes[1]}
                                fontWeight={ClinTheme.fontWeights.medium}
                                lineHeight={'24px'}
                                color={ClinTheme.colors.primaryMid}
                              >
                                {formatDeliveryDate(latestDate, i18n.language)}
                              </ClinText>
                              <Tooltip
                                theme="light"
                                position="bottom"
                                trigger="mouseenter"
                                arrow={true}
                                disabled={
                                  hasAftershipTracking(
                                    orderLineWithLatestDate
                                  ) &&
                                  !!orderLineWithLatestDate.trackingInfo
                                    ?.aftershipTrackingLink
                                }
                                title={
                                  hasAftershipTracking(
                                    orderLineWithLatestDate
                                  ) &&
                                  !!orderLineWithLatestDate.trackingInfo
                                    ?.aftershipTrackingLink
                                    ? ''
                                    : t('order_detail:tracking_tooltip')
                                }
                              >
                                <StyledOrderDetailEstimatedDeliveryDateButton
                                  className="tracking-link-button"
                                  variant={
                                    hasAftershipTracking(
                                      orderLineWithLatestDate
                                    ) &&
                                    !!orderLineWithLatestDate.trackingInfo
                                      ?.aftershipTrackingLink
                                      ? 'link'
                                      : 'linkButton'
                                  }
                                  color={ClinTheme.colors.primaryMid}
                                  disabled={false}
                                  onClick={() => {
                                    handleTrackingLinkClick(
                                      orderLineWithLatestDate
                                    )
                                  }}
                                >
                                  {showTrackingInfo(orderLineWithLatestDate)}
                                </StyledOrderDetailEstimatedDeliveryDateButton>
                              </Tooltip>
                            </Fragment>
                          </StyledOrderDetailDeliveryDateContent>
                        </StyledOrderDetailDeliveryDateContainer>
                      )
                    })()}
                  </StyledOrderDetailHeaderContent>
                </StyledOrderDetailHeaderContainer>
              </StyledHeaderClinPageContentFrame>
            </OrderDetailHeaderContainer>
            <ClinPageContentFrame>
              {orderAnnounce && (
                <>
                  {orderAnnounce.mode === AnnounceMode.Success ? (
                    <ClinToastBar
                      handleClose={handleDismissAnnounce}
                      mode={orderAnnounce.mode}
                      title={orderAnnounce.title}
                    >
                      {orderAnnounce.message}
                    </ClinToastBar>
                  ) : (
                    <ClinAnnounceBar
                      handleClose={handleDismissAnnounce}
                      mode={orderAnnounce.mode}
                      title={orderAnnounce.title}
                    >
                      {orderAnnounce.message}
                    </ClinAnnounceBar>
                  )}
                  <ClinSpacer height={ClinTheme.space[4]} />
                </>
              )}
              <Row onClick={(e) => e.stopPropagation()}>
                <Col id="order-detail-ma-documents-announce-bar-container" />
              </Row>
              <Row>
                <Col>
                  <StyledOrderBar>
                    <ClinText
                      fontSize={14}
                      fontWeight={700}
                      lineHeight={'20px'}
                      letterSpacing={0}
                    >
                      {t('ma_order_detail:order_details_card.title')}
                    </ClinText>
                  </StyledOrderBar>
                  <StyledDetailsContainer>
                    <StyledDetailsItem>
                      <ClinText
                        color={ClinTheme.colors.darkGrey}
                        fontSize={12}
                        fontWeight={ClinTheme.fontWeights.medium}
                        marginBottom={ClinTheme.space[2]}
                        lineHeight={'18px'}
                        letterSpacing={0}
                      >
                        {t(
                          'ma_order_detail:order_details_card.patient_id_label'
                        )}
                      </ClinText>
                      <ClinText
                        fontSize={14}
                        fontWeight={500}
                        color={ClinTheme.colors.black}
                        lineHeight={'20px'}
                        letterSpacing={0}
                      >
                        {isPhysicianAssociated && isEnrolledProgram
                          ? patientNumber ||
                            t('glossary:not_applicable_long_hand')
                          : t('glossary:confidential')}
                      </ClinText>
                    </StyledDetailsItem>
                    <StyledDetailsItem>
                      <ClinText
                        color={ClinTheme.colors.darkGrey}
                        fontSize={12}
                        fontWeight={ClinTheme.fontWeights.medium}
                        marginBottom={ClinTheme.space[2]}
                        lineHeight={'18px'}
                        letterSpacing={0}
                      >
                        {t(
                          'ma_order_detail:order_details_card.treating_physician_label'
                        )}
                      </ClinText>
                      <ClinText
                        fontSize={14}
                        fontWeight={500}
                        color={ClinTheme.colors.black}
                        lineHeight={'20px'}
                        letterSpacing={0}
                      >
                        {isPhysicianAssociated && isEnrolledProgram
                          ? `${physician?.physicianTitle ?? ''} ${
                              physician?.physicianFirstName
                            } ${physician?.physicianLastName}`
                          : t('glossary:confidential')}
                      </ClinText>
                    </StyledDetailsItem>
                    <StyledDetailsItem>
                      <ClinText
                        color={ClinTheme.colors.darkGrey}
                        fontSize={12}
                        fontWeight={ClinTheme.fontWeights.medium}
                        marginBottom={ClinTheme.space[2]}
                        lineHeight={'18px'}
                        letterSpacing={0}
                      >
                        {t(
                          'ma_order_detail:order_details_card.patient_access_form_label'
                        )}
                      </ClinText>
                      {isPhysicianAssociated && isEnrolledProgram ? (
                        <StyleOpaAction
                          onClick={
                            isPhysicianAssociated
                              ? handlePatientAccessForm
                              : undefined
                          }
                        >
                          <ClinText
                            fontSize={ClinTheme.fontSizes[2]}
                            fontWeight={ClinTheme.fontWeights.medium}
                            color={ClinTheme.colors.tealishBlue}
                          >
                            {patientAccessFormUrl
                              ? t(
                                  'ma_order_detail:order_details_card.view_form'
                                )
                              : t(
                                  'ma_order_detail:order_details_card.access_form_error'
                                )}
                          </ClinText>
                        </StyleOpaAction>
                      ) : (
                        <ClinText
                          as="p"
                          variant={TypographyVariant.LargeParagraph}
                        >
                          {t('glossary:confidential')}
                        </ClinText>
                      )}
                    </StyledDetailsItem>
                    <StyledDetailsItem>
                      <ClinText
                        color={ClinTheme.colors.darkGrey}
                        fontSize={12}
                        fontWeight={ClinTheme.fontWeights.medium}
                        marginBottom={ClinTheme.space[2]}
                        lineHeight={'18px'}
                        letterSpacing={0}
                      >
                        {t(
                          'ma_order_detail:order_details_card.submitted_by_label'
                        )}
                      </ClinText>
                      <ClinText
                        fontSize={14}
                        fontWeight={500}
                        color={ClinTheme.colors.black}
                        lineHeight={'20px'}
                        letterSpacing={0}
                      >
                        {order?.shipToContact ?? t('glossary:not_applicable')}
                      </ClinText>
                    </StyledDetailsItem>
                  </StyledDetailsContainer>
                </Col>
              </Row>
              <ClinSpacer height={ClinTheme.space[4]} />
              <Row>
                <Col>
                  <OrderDelivery order={order} isMAOrder />
                </Col>
                <Col
                  style={{
                    maxWidth: '360px',
                    paddingLeft: ClinTheme.space[2],
                    boxSizing: 'content-box'
                  }}
                >
                  <OrderSummary order={order} isMAOrder />
                </Col>
              </Row>
              <ClinSpacer height={ClinTheme.space[4]} />
              {reassignedItems.length > 0 && (
                <>
                  <ReassignedProducts
                    order={order}
                    programSponsor={program?.sponsor}
                  />
                  <ClinSpacer height={ClinTheme.space[4]} />
                </>
              )}
              <StyledProductDetailsSection>
                <ClinText
                  as="span"
                  color={ClinTheme.colors.black}
                  fontWeight={ClinTheme.fontWeights.bold}
                  fontSize={ClinTheme.fontSizes[1]}
                >
                  {t('order_detail:product_details')}
                </ClinText>
                <StyledActions>
                  {shouldShowCancel(filteredOrderLines) && (
                    <StyledClinSecondaryButton
                      variant="secondary"
                      onClick={handleCancelOrder}
                    >
                      {t('ma_order_detail:cancel_order_button')}
                    </StyledClinSecondaryButton>
                  )}
                  <StyledClinPrimaryButton
                    variant="primary"
                    disabled={isDisableResupply(order)}
                    onClick={handleOrderResupply}
                  >
                    {t('ma_order_detail:order_resupply')}
                  </StyledClinPrimaryButton>
                </StyledActions>
              </StyledProductDetailsSection>
              <StyledDesktopWrapper>
                <StyledClinTable
                  borderColapseSeparate={true}
                  className="TableOverflowVisible"
                  tableHeader={() =>
                    columns.map((column) => (
                      <StyledClinPatientTableHeaderCell
                        key={`th-${column.name}`}
                        background={ClinTheme.colors.lightGrey}
                      >
                        <FixedWidthCell
                          width={column.width}
                          minWidth={column.minWidth}
                          maxWidth={column.maxWidth}
                        >
                          {column.isSortable ? (
                            <StyledClinTableOrderToggleWrapper>
                              <ClinTableOrderToggle isUppercase={false}>
                                {column.viewName}
                              </ClinTableOrderToggle>
                            </StyledClinTableOrderToggleWrapper>
                          ) : (
                            <StyledClinTableOrderToggleWrapper>
                              <ClinTableOrderToggle hideSortIcon={true}>
                                {column.viewName}
                              </ClinTableOrderToggle>
                            </StyledClinTableOrderToggleWrapper>
                          )}
                        </FixedWidthCell>
                      </StyledClinPatientTableHeaderCell>
                    ))
                  }
                >
                  {filteredOrderLines
                    ?.filter((ol) => ol.medicationFlag === 'Y')
                    ?.map((orderLine: OrderLineDto, index: number) => (
                      <Fragment
                        key={`variant-row-${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}-${index}`}
                      >
                        {/* Main Row */}
                        <StyledClinTableRow
                          background={ClinTheme.colors.white}
                          className={`main-row ${
                            hoveredRow ===
                            `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                              ? 'highlighted'
                              : ''
                          } ${
                            expandedRows.includes(
                              `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                            )
                              ? 'expanded'
                              : ''
                          }`}
                          onMouseEnter={() =>
                            handleMouseEnter(
                              `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                            )
                          }
                          onMouseLeave={handleMouseLeave}
                        >
                          {columns.map((column) => (
                            <StyledClinTableBodyCell
                              key={`${column.name}-${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`}
                              rowExpanded={expandedRows.includes(
                                `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                              )}
                              onClick={() =>
                                handleSelectProduct &&
                                handleSelectProduct(orderLine)
                              }
                            >
                              <FixedWidthCell
                                width={column.width}
                                minWidth={column.minWidth}
                                maxWidth={column.maxWidth}
                              >
                                {column.renderValue
                                  ? column.renderValue(orderLine)
                                  : null}
                              </FixedWidthCell>
                            </StyledClinTableBodyCell>
                          ))}
                        </StyledClinTableRow>

                        {/* Expanded Row */}
                        <StyledClinTableExpandedRow
                          background={ClinTheme.colors.white}
                          isOpen={expandedRows.includes(
                            `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                          )}
                          className={`expanded-row ${
                            hoveredRow ===
                            `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                              ? 'highlighted'
                              : ''
                          }`}
                          onMouseEnter={() =>
                            handleMouseEnter(
                              `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                            )
                          }
                          onMouseLeave={handleMouseLeave}
                        >
                          <StyledClinTableBodyCell
                            colSpan={10}
                            style={{ padding: '16px' }}
                            onClick={() =>
                              handleSelectProduct &&
                              handleSelectProduct(orderLine)
                            }
                          >
                            <StyledProductInformationBox>
                              <ProductInformationBox>
                                <ProductInformation
                                  handleFileUploadedForOrderAndHold={
                                    handleFileUploadedForOrderAndHold
                                  }
                                  handleFileDeleteForOrderAndHold={
                                    handleFileDeleteForOrderAndHold
                                  }
                                  orderId={order.orderNumber}
                                  orderLines={order?.lines}
                                  orderStatus={order.orderStatus}
                                  orderLine={orderLine}
                                  programSponsor={program?.sponsor}
                                  isEnrolledProgram={isEnrolledProgram}
                                />
                              </ProductInformationBox>
                            </StyledProductInformationBox>
                          </StyledClinTableBodyCell>
                        </StyledClinTableExpandedRow>

                        {/* Empty Row */}
                        <StyledClinTableRow disabled>
                          <ClinTableBodyCell
                            colSpan={10}
                            style={{ padding: '4px' }}
                          ></ClinTableBodyCell>
                        </StyledClinTableRow>
                      </Fragment>
                    ))}
                </StyledClinTable>
              </StyledDesktopWrapper>
            </ClinPageContentFrame>
          </StyledOrderDetail>
        </ClinPageContentFrame>
      )}
    </ClinPageContentFrame>
  )
}
